import React, { Component } from 'react'
import { withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Chip from '@material-ui/core/Chip'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'
import Divider from '@material-ui/core/Divider'
import InputAdornment from '@material-ui/core/InputAdornment'
import Icon from '@material-ui/core/Icon'
import { CircularProgress } from '@material-ui/core'
import gql from 'graphql-tag'
import { Query, withApollo } from 'react-apollo'
import ConfirmationalDialog from '../../common/ConfirmationDialog'
import withSharedSnackbar from '../../HOCs/withSharedSnackbar'

const styles = () => ({
  root: {
    padding: 24,
    marginTop: 24
  },
  paper: {
    padding: 0,
    marginBottom: 16,
    overflow: 'hidden'
  },
  tabContent: {
    padding: 20
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 12,
    color: '#2c3e50'
  },
  vehicleListSection: {
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 16
  },
  vehicleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px 6px 8px',
    borderBottom: '1px solid #f5f5f5',
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: '#fafafa' }
  },
  chipActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
    fontSize: 11
  },
  chipInactive: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef9a9a',
    fontSize: 11
  },
  activateBtn: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    marginTop: 12,
    '&:hover': { backgroundColor: '#1b5e20' },
    '&:disabled': { backgroundColor: '#bdbdbd', color: '#fff' }
  },
  deactivateBtn: {
    backgroundColor: '#c62828',
    color: '#fff',
    marginTop: 12,
    '&:hover': { backgroundColor: '#7f0000' },
    '&:disabled': { backgroundColor: '#bdbdbd', color: '#fff' }
  },
  emptyState: {
    padding: 32,
    textAlign: 'center',
    color: '#999'
  },
  searchBar: {
    padding: '10px 16px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff'
  }
})

// status: 0 → inactive vehicles (shown in Activate tab)
// status: 1 → active vehicles (shown in Deactivate tab)
const GET_VEHICLES = gql`
  query getAllVehicleDetailsByStatus($loginId: Int, $status: Int) {
    vehicles: getAllVehicleDetailsByStatus(
      partnerLoginId: $loginId
      status: $status
    ) {
      entityId
      vehicleNumber
      status
      deviceDetail {
        uniqueDeviceId
      }
    }
  }
`

const BULK_ACTIVATE_DEACTIVATE = gql`
  mutation activateDeactivateVehiclesonboarding(
    $uniqueids: [String!]!
    $entityids: [Int!]!
    $action: String!
    $reason: String!
    $created_by: String
    $updated_by: String
  ) {
    activateDeactivateVehiclesonboarding(
      uniqueids: $uniqueids
      entityids: $entityids
      action: $action
      reason: $reason
      created_by: $created_by
      updated_by: $updated_by
    ) {
      success
      message
    }
  }
`

const HARDCODED_LOGIN_ID = 8148

class VehicleDeactivationActivation extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeTab: 0,
      selectedVehicles: [],
      searchQuery: '',
      reason: '',
      isSubmitting: false,
      confirmDialogOpen: false,
      confirmDialogMessage: '',
      confirmDialogTitle: '',
      pendingAction: null
    }
  }

  handleTabChange = (event, value) => {
    this.setState({
      activeTab: value,
      selectedVehicles: [],
      reason: '',
      searchQuery: ''
    })
  }

  handleSearchChange = e => {
    this.setState({ searchQuery: e.target.value })
  }

  clearSearch = () => {
    this.setState({ searchQuery: '' })
  }

  handleToggleVehicle = vehicle => {
    const { selectedVehicles } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const exists = safeSelected.find(v => v.value === vehicle.value)
    if (exists) {
      this.setState({ selectedVehicles: safeSelected.filter(v => v.value !== vehicle.value) })
    } else {
      this.setState({ selectedVehicles: [...safeSelected, vehicle] })
    }
  }

  submitAction = () => {
    const { activeTab, selectedVehicles, reason } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const action = activeTab === 0 ? 'Activate' : 'Deactivate'

    if (safeSelected.length === 0) {
      this.props.openSnackbar('Please select at least one vehicle')
      return
    }
    if (!reason || reason.trim() === '') {
      this.props.openSnackbar('Please provide a reason')
      return
    }

    const vehicleLabels = safeSelected.map(v => v.vehicleNumber).join(', ')
    this.setState({
      confirmDialogOpen: true,
      confirmDialogTitle: `Confirm ${action}`,
      confirmDialogMessage: `Are you sure you want to ${action.toLowerCase()} ${safeSelected.length} vehicle(s)?\n\n${vehicleLabels}`,
      pendingAction: action
    })
  }

  executeAction = async () => {
    const { pendingAction, selectedVehicles, reason } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []

    const uniqueids = safeSelected
      .map(v => v.deviceDetail && v.deviceDetail.uniqueDeviceId)
      .filter(id => id)

    const entityids = safeSelected
      .map(v => parseInt(v.entityId || v.value, 10))
      .filter(id => !isNaN(id))

    this.setState({ isSubmitting: true, confirmDialogOpen: false })

    try {
      const { data } = await this.props.client.mutate({
        mutation: BULK_ACTIVATE_DEACTIVATE,
        variables: {
          uniqueids,
          entityids,
          action: pendingAction,
          reason: reason.trim(),
          created_by: localStorage.getItem('username'),
          updated_by: localStorage.getItem('username')
        },
        refetchQueries: ['getAllVehicleDetailsByStatus']
      })
      const response = data && data.activateDeactivateVehiclesonboarding
      this.props.openSnackbar((response && response.message) || `${pendingAction} submitted for ${uniqueids.length} vehicle(s)`)
      this.setState({ selectedVehicles: [], reason: '', searchQuery: '' })
    } catch (e) {
      console.error('Action error:', e)
      this.props.openSnackbar(`Failed to ${pendingAction} vehicle(s)`)
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  closeConfirmDialog = () => {
    this.setState({ confirmDialogOpen: false })
  }

  renderVehicleList = (allVehicles, chipLabel, chipClass, classes) => {
    const { selectedVehicles, searchQuery } = this.state
    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const query = searchQuery.trim().toLowerCase()

    // Filter vehicles by search query
    const filtered = query
      ? allVehicles.filter(v => {
        const matchVehicle = v.vehicleNumber && v.vehicleNumber.toLowerCase().includes(query)
        const matchDevice =
          v.deviceDetail &&
          v.deviceDetail.uniqueDeviceId &&
          v.deviceDetail.uniqueDeviceId.toLowerCase().includes(query)
        return matchVehicle || matchDevice
      })
      : allVehicles

    if (allVehicles.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">No vehicles found</Typography>
        </div>
      )
    }

    return (
      <div className={classes.vehicleListSection}>

        {/* Search Bar */}
        <div className={classes.searchBar}>
          <TextField
            placeholder="Search by vehicle number or device ID..."
            value={searchQuery}
            onChange={this.handleSearchChange}
            fullWidth
            variant="outlined"
            size="small"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon style={{ color: '#aaa', fontSize: 20 }}>search</Icon>
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Icon
                    style={{ color: '#aaa', fontSize: 18, cursor: 'pointer' }}
                    onClick={this.clearSearch}
                  >
                    close
                  </Icon>
                </InputAdornment>
              ) : null
            }}
          />
          {query && (
            <Typography variant="caption" style={{ color: '#666', marginTop: 4, display: 'block' }}>
              Showing {filtered.length} of {allVehicles.length} vehicles
              {safeSelected.length > 0 && ` • ${safeSelected.length} total selected`}
            </Typography>
          )}
        </div>

        {/* Vehicle Rows */}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" style={{ color: '#aaa' }}>
                No vehicles match &quot;{searchQuery}&quot;
              </Typography>
            </div>
          ) : (
            filtered.map(vehicle => {
              const isChecked = safeSelected.some(v => v.value === vehicle.value)
              return (
                <div
                  key={vehicle.value}
                  className={classes.vehicleRow}
                  onClick={() => this.handleToggleVehicle(vehicle)}
                  style={{ backgroundColor: isChecked ? '#f3f8ff' : 'transparent' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={isChecked}
                      color="primary"
                      style={{ padding: '0 8px 0 0' }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => this.handleToggleVehicle(vehicle)}
                    />
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {vehicle.vehicleNumber}
                      </Typography>
                      {vehicle.deviceDetail && vehicle.deviceDetail.uniqueDeviceId && (
                        <Typography variant="caption" style={{ color: '#888' }}>
                          {vehicle.deviceDetail.uniqueDeviceId}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <Chip label={chipLabel} size="small" className={chipClass} />
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  render() {
    const { classes } = this.props
    const {
      activeTab,
      selectedVehicles,
      reason,
      isSubmitting,
      confirmDialogOpen,
      confirmDialogMessage,
      confirmDialogTitle
    } = this.state

    const safeSelected = Array.isArray(selectedVehicles) ? selectedVehicles : []
    const isActivateTab = activeTab === 0
    const queryStatus = isActivateTab ? 0 : 1
    const action = isActivateTab ? 'Activate' : 'Deactivate'
    const btnClass = isActivateTab ? classes.activateBtn : classes.deactivateBtn
    const chipLabel = isActivateTab ? 'Inactive' : 'Active'
    const chipClass = isActivateTab ? classes.chipInactive : classes.chipActive

    return (
      <div className={classes.root}>
        <Typography variant="h4" component="h1" gutterBottom>
          Vehicle Deactivation / Activation
        </Typography>

        <Paper className={classes.paper}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            style={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <Tab
              label="Activate Vehicles"
              style={{ color: activeTab === 0 ? '#2e7d32' : undefined, fontWeight: 600 }}
            />
            <Tab
              label="Deactivate Vehicles"
              style={{ color: activeTab === 1 ? '#c62828' : undefined, fontWeight: 600 }}
            />
          </Tabs>

          <div className={classes.tabContent}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {isActivateTab
                ? 'Search and select inactive vehicles to activate'
                : 'Search and select active vehicles to deactivate'}
              {safeSelected.length > 0 && (
                <span style={{ fontWeight: 400, fontSize: 13, color: '#555', marginLeft: 8 }}>
                  ({safeSelected.length} selected)
                </span>
              )}
            </Typography>

            {/* Vehicle Checkbox List with Search */}
            <Query
              query={GET_VEHICLES}
              variables={{ loginId: HARDCODED_LOGIN_ID, status: queryStatus }}
              fetchPolicy="network-only"
            >
              {({ loading, error, data }) => {
                if (loading) {
                  return (
                    <div style={{ padding: 32, textAlign: 'center' }}>
                      <CircularProgress />
                    </div>
                  )
                }
                if (error) {
                  return (
                    <div style={{ padding: 16, color: 'red' }}>
                      Error loading vehicles
                    </div>
                  )
                }

                const vehicleOptions = (data.vehicles || []).map(v => ({
                  value: v.entityId,
                  entityId: v.entityId,
                  vehicleNumber: v.vehicleNumber,
                  status: v.status,
                  deviceDetail: v.deviceDetail
                }))

                return this.renderVehicleList(vehicleOptions, chipLabel, chipClass, classes)
              }}
            </Query>

            {/* Reason & Submit — only visible when vehicles are selected */}
            {safeSelected.length > 0 && (
              <React.Fragment>
                <Divider style={{ marginBottom: 16 }} />
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={9}>
                    <TextField
                      label={`Reason for ${action} *`}
                      placeholder="Enter reason..."
                      value={reason}
                      onChange={e => this.setState({ reason: e.target.value })}
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      fullWidth
                      className={btnClass}
                      disabled={isSubmitting || !reason.trim()}
                      onClick={this.submitAction}
                    >
                      {isSubmitting
                        ? <CircularProgress size={20} style={{ color: '#fff' }} />
                        : `${action} (${safeSelected.length})`}
                    </Button>
                  </Grid>
                </Grid>
              </React.Fragment>
            )}
          </div>
        </Paper>

        {/* Confirm Dialog */}
        <ConfirmationalDialog
          isDialogOpen={confirmDialogOpen}
          dialogTitle={confirmDialogTitle}
          dialogMessage={confirmDialogMessage}
          positiveResponseButtonText="Yes, Proceed"
          negativeResponseButtonText="Cancel"
          positiveResponseHandler={this.executeAction}
          negativeResponseHandler={this.closeConfirmDialog}
          dialogCloseHandler={this.closeConfirmDialog}
        />
      </div>
    )
  }
}

export default withStyles(styles)(
  withApollo(withSharedSnackbar(VehicleDeactivationActivation))
)



// WEBPACK FOOTER //
// ./src/components/Pages/VehicleDeactivationActivation/index.jsx